import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import { seo } from '../seo/seoData';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Loader2, Car } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

const API_URL = '/api';

const DriverLoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/driver/login`, formData);
      
      // Store driver token and info
      localStorage.setItem('driverToken', response.data.access_token);
      localStorage.setItem('driverInfo', JSON.stringify(response.data.driver));
      
      navigate('/driver');
    } catch (err) {
      setError(err.response?.data?.detail || 'Identifiants invalides');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#2B2B2B] flex items-center justify-center px-4" data-testid="driver-login-page">
          <Seo {...seo.driver} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-black border border-white/10 rounded-2xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Car className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-2">
              Espace Chauffeur
            </h1>
            <p className="text-white/50 text-sm">
              Connectez-vous pour gérer vos courses
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-xl mb-6 text-sm"
              data-testid="login-error"
            >
              {error}
            </motion.div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="chauffeur@blackmatte.fr"
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 focus:border-white/20 rounded-xl"
                  required
                  data-testid="driver-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-400">
                Mot de passe
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="••••••••"
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-neutral-500 focus:border-white/20 rounded-xl"
                  required
                  data-testid="driver-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white border border-white/15 hover:bg-white/5 hover:border-white/25 py-6 text-lg font-medium rounded-xl transition-all"
              data-testid="driver-login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Connexion...
                </>
              ) : (
                'Se connecter'
              )}
            </Button>
          </form>

          {/* Demo credentials */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <p className="text-xs text-neutral-500 text-center">
              Compte test: driver@blackmatte.fr / driver123
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DriverLoginPage;
